import { EventQuery } from 'queries';
import { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { DEFAULT_ERROR_MESSAGE } from 'data/message';
import { IEvent } from 'types';

type Type = keyof Pick<IEvent, 'description' | 'notice' | 'feedbackGuide'>;

interface Props {
  event: IEvent | null;
}

function useEditor({ event }: Props) {
  const { mutate } = EventQuery.useUpdate();
  const [value, setValue] = useState('');
  const [isOpenEditor, setIsOpenEditor] = useState(false);
  const [type, setType] = useState<Type>('description');

  const title = useMemo(() => {
    if (type === 'description') return '행사 상세';
    if (type === 'notice') return '행사 공지사항';
    return '피드백 가이드';
  }, [type]);

  const handleOpen = (type: Type) => {
    setIsOpenEditor(true);
    setType(type);
    const value = event?.[type] || '';
    setValue(value);
  };

  const handleClose = () => setIsOpenEditor(false);

  const handleDesc = (data: string) => {
    if (!event?.id) return;
    mutate(
      { id: event.id, description: data, isNewFile: false },
      {
        onSuccess: () => {
          toast.success('상세정보가 저장되었습니다.');
          handleClose();
        },
        onError: () => {
          toast.error(DEFAULT_ERROR_MESSAGE);
        },
      },
    );
  };

  const handleNotice = (data: string) => {
    if (!event?.id) return;
    mutate(
      { id: event.id, notice: data, isNewFile: false },
      {
        onSuccess: () => {
          toast.success('유의사항이 저장되었습니다.');
          handleClose();
        },
        onError: () => {
          toast.error(DEFAULT_ERROR_MESSAGE);
        },
      },
    );
  };

  const handleFeedbackGuide = (data: string) => {
    if (!event?.id) return;
    mutate(
      { id: event.id, feedbackGuide: data, isNewFile: false },
      {
        onSuccess: () => {
          toast.success('피드백 가이드가 저장되었습니다.');
          handleClose();
        },
        onError: () => {
          toast.error(DEFAULT_ERROR_MESSAGE);
        },
      },
    );
  };

  const handleSaveEditor = (data: string) => {
    if (type === 'description') handleDesc(data);
    else if (type === 'notice') handleNotice(data);
    else if (type === 'feedbackGuide') handleFeedbackGuide(data);
  };

  return {
    title,
    value,
    isOpenEditor,
    handleOpen,
    handleClose,
    handleSaveEditor,
  };
}

export default useEditor;
